import type { BenefitDetailDto, BenefitEligibilityDto, BenefitGrantDto, BenefitWorkflowResponse, SelectableGrantProgramDto } from "./benefit-types"
import { canCorrectDraftGrant } from "./grant-correction"
import { availableGrantEligibility, grantInstanceKey } from "./grant-options"
import { usableGrantAuthority } from "./grant-workspace"
import { captureVendorPaymentContext } from "./vendor-payment-context"

/** Only authoring prerequisites. Saved data and authority still come from the current workspace. */
export interface GrantAuthoringContext {
  id: string
  token: string
  authority: "Legacy" | "V2"
  options: BenefitEligibilityDto[]
  generationId: string | null
  reviewVersion: number | null
}

export function captureGrantAuthoringContext(
  benefit: BenefitDetailDto, workflow: BenefitWorkflowResponse | null,
  programs: readonly SelectableGrantProgramDto[], grant: BenefitGrantDto | null,
): GrantAuthoringContext | null {
  const legacy = workflow?.authority === "Legacy" && workflow.display?.authority === "Legacy" && workflow.display.readUnavailable === false
  if (!benefit.concurrencyToken || (!legacy && !usableGrantAuthority(workflow))) return null
  const correction = grant ? captureVendorPaymentContext(benefit, grant, workflow) : null
  if (grant && (!canCorrectDraftGrant(grant) || !correction)) return null
  const options = grant
    ? benefit.benefitEligibility.filter(option => option.isEligible && grantInstanceKey(option.benefitProgramInstance) === grantInstanceKey(grant.benefitProgramInstance))
    : availableGrantEligibility(benefit.benefitEligibility, benefit.benefitGrants, new Set(programs.map(program => program.id)))
  return { id: benefit.id, token: benefit.concurrencyToken, authority: legacy ? "Legacy" : "V2",
    options: structuredClone(options), generationId: correction?.generationId ?? null, reviewVersion: correction?.reviewVersion ?? null }
}

/** No refreshed calculation, grant generation or payment prerequisite may be attached to an old draft. */
export function grantAuthoringContextCurrent(
  context: GrantAuthoringContext | null, grant: BenefitGrantDto | null, selectedInstance: string,
  benefit: BenefitDetailDto, workflow: BenefitWorkflowResponse | null, programs: readonly SelectableGrantProgramDto[],
): boolean {
  if (!context || context.id !== benefit.id || context.token !== benefit.concurrencyToken) return false
  const current = captureGrantAuthoringContext(benefit, workflow, programs, grant)
  if (!current || current.authority !== context.authority || current.generationId !== context.generationId || current.reviewVersion !== context.reviewVersion) return false
  const selected = context.options.filter(option => grantInstanceKey(option.benefitProgramInstance) === selectedInstance)
  if (selected.length !== 1) return false
  const original = selected[0]!
  // Endpoints address program/year. Multiple revisions/duplicate calculations are not an unambiguous choice.
  const matches = benefit.benefitEligibility.filter(option => option.benefitProgramInstance.program === original.benefitProgramInstance.program && option.benefitProgramInstance.year === original.benefitProgramInstance.year)
  if (matches.length !== 1 || JSON.stringify(matches[0]) !== JSON.stringify(original)) return false
  if (!grant && programs.filter(program => program.id === original.benefitProgramInstance.program).length !== 1) return false
  return current.options.some(option => grantInstanceKey(option.benefitProgramInstance) === selectedInstance)
}
